import React, { useState, useEffect, useCallback } from 'react';
import { Modal as ModalPopup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { I18n } from '../../../helpers/i18n';
import client from '../../../helpers/client';
import mvpAuth from '../../../helpers/mvpAuth';
import starActive from '../../../static/images/mvp/star_active.png';
import starInactive from '../../../static/images/mvp/star_in_active.png';
import './SessionRatingModal.css';

const STAR_LABELS = [
  'ratting_one',
  'ratting_two',
  'ratting_three',
  'ratting_four',
  'ratting_five',
];

function SessionRatingModal({ visible, sessionId, onClose, onSuccess, isBuddy = false, initialScore = 0 }) {
  const [score, setScore] = useState(0);
  const [hoverScore, setHoverScore] = useState(0);
  const [satisfactionOptions, setSatisfactionOptions] = useState([]);
  const [allOptions, setAllOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [extraComment, setExtraComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const prefix = isBuddy ? 'buddy_session_ratting' : 'senior_session_ratting';

  const fetchOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const data = await client.sendRequest({
        path: 'mvp/list-data-evaluate-session',
        method: 'GET',
        headers: mvpAuth.getHeaderForMvp(),
      });
      if (data?.data) {
        setAllOptions(data.data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    if (visible) {
      fetchOptions();
      setScore(initialScore || 0);
      setHoverScore(0);
      setSelectedOptions([]);
      setExtraComment('');
    }
  }, [visible, fetchOptions, initialScore]);

  useEffect(() => {
    if (score > 0 && allOptions.length > 0) {
      const group = allOptions.find((g) => g.score === score);
      setSatisfactionOptions(group ? group.list : []);
      setSelectedOptions([]);
    } else {
      setSatisfactionOptions([]);
    }
  }, [score, allOptions]);

  const toggleOption = (value) => {
    setSelectedOptions((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleSubmit = async () => {
    if (score === 0 || submitting) return;
    setSubmitting(true);
    try {
      await client.sendRequest({
        path: 'mvp/evaluate-session',
        method: 'POST',
        params: {
          sessionId,
          score,
          content: selectedOptions,
          contentExtra: extraComment || '',
        },
        headers: mvpAuth.getHeaderForMvp(),
      });
      toast.success(I18n.t('ratting_success'), {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        draggable: false,
        progress: undefined,
        theme: 'colored',
      });
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.message || I18n.t('something_wrong'), {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        draggable: false,
        progress: undefined,
        className: 'toast-error-container toast-error-container-after',
        theme: 'colored',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const displayScore = hoverScore || score;

  return (
    <ModalPopup
      size="lg"
      show={visible}
      onHide={onClose}
      id="session-rating-modal"
      style={{ opacity: 1 }}
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <div className="rating-modal-header">
        <span className="rating-modal-close" onClick={onClose}>✕</span>
        <span className="rating-modal-header-title">{I18n.t('session_ratings_list_bottom_sheet_title')}</span>
      </div>
      <ModalPopup.Body>
        <div className="rating-modal-content">
          <h4 className="rating-modal-title">{I18n.t(`${prefix}_title`)}</h4>
          <p className="rating-modal-description">{I18n.t(`${prefix}_des`)}</p>

          <div className="rating-stars-container">
            <div className="rating-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className="rating-star"
                  onClick={() => setScore(star)}
                  onMouseEnter={() => setHoverScore(star)}
                  onMouseLeave={() => setHoverScore(0)}
                >
                  <img
                    src={displayScore >= star ? starActive : starInactive}
                    alt=""
                    className="rating-star-img"
                  />
                </button>
              ))}
            </div>
            <span className="rating-label">
              {displayScore > 0 ? I18n.t(STAR_LABELS[displayScore - 1]) : I18n.t('ratting_none')}
            </span>
          </div>

          {score > 0 && (
            <>
              <div className="rating-satisfaction-section">
                <div className="rating-satisfaction-header">
                  <p className="rating-satisfaction-title">
                    {score >= 4 ? I18n.t(`${prefix}_selection_title`) : I18n.t(`${prefix}_selection_title_2`)}
                  </p>
                  <p className="rating-satisfaction-hint">{I18n.t(`${prefix}_selection_des`)}</p>
                </div>

                {loadingOptions ? (
                  <div className="rating-loading">...</div>
                ) : (
                  <div className="rating-options">
                    {satisfactionOptions.map((option, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`rating-option-chip ${selectedOptions.includes(option.value) ? 'selected' : ''}`}
                        onClick={() => toggleOption(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="rating-extra-comment-section">
                <p className="rating-extra-comment-title">{I18n.t(`${prefix}_extra_comment`)}</p>
                <textarea
                  className="rating-extra-comment-input"
                  rows="3"
                  placeholder={I18n.t('senior_session_ratting_extra_comment_hint_text')}
                  value={extraComment}
                  onChange={(e) => setExtraComment(e.target.value)}
                />
              </div>

              <div className="rating-modal-divider" />

              <div className="rating-submit-row">
                <button
                  className={`rating-submit-btn ${score === 0 || submitting ? 'disabled' : ''}`}
                  onClick={handleSubmit}
                  disabled={score === 0 || submitting}
                >
                  {submitting ? '...' : I18n.t(`${prefix}_bottom_cta_title`)}
                </button>
              </div>
            </>
          )}
        </div>
      </ModalPopup.Body>
    </ModalPopup>
  );
}

export default SessionRatingModal;
